import React, { useMemo, useRef } from 'react';
import { AppSkinProps } from '../../../types/decisions';
import Modal from '../../modal/Modal';
import CriteriaSetting from '../app-components/CriteriaSetting';
import BubbleChart from '../../bubble-chart/BubbleChart';
import AppResults from '../app-components/AppResults/AppResults';
import Loading from '../../loading/Loading';
import { useAppConfig } from '../../../context/app-configs-context';
import AppInfo from '../app-components/AppInfo';
import { getCriteriaFieldsToDisplayInCriteria, getCriteriaFieldsToDisplayInResults } from '../../../utils';
import { useSearchParams } from 'react-router-dom';

const AppSkin7 = ({
    results,
    onRerank,
    onRerankMany,
    criteriaLoading,
    criteriaData,
    criteriaConstraints,
    onSliderChange,
    onCriteriaApply,
    resultLoading,
    onPageChange,
    pageInfo,
    onTradeoffValueChange,
    fieldInitialValues,
    selectedProductIds,
    setSelectedProductIds,
}: AppSkinProps) => {
    const [ searchParams ] = useSearchParams();
    const dataset = searchParams.get('dataset') || 'main';
    const {
        appConfigs: {
            inferenceRank,
            criteriaSettings: { popup },
        },
    } = useAppConfig();
    const [ open, setOpen ] = React.useState(false);
    const resultsDivRef = useRef<HTMLDivElement>(null);
    const bubbleChartRef = useRef<HTMLDivElement>(null);
    const [ modalContent, setModalContent ] = React.useState<React.ReactElement>(<></>);
    const criteriaChangedOnceRef = useRef<{
        [key: string]: boolean
    }>({});
    const shouldShowRankByManyCheckbox = inferenceRank === 'ThumbsUp';
    const displayFieldsIds: string[] = useMemo(() => {
        return getCriteriaFieldsToDisplayInResults(criteriaData.fields)
        .map(item => item.id);
    }, [ criteriaData.fields ]);


    const onAutoPopupCheck = (newImportance: number, id: string) => {
        if (!popup.show || newImportance < popup.threshold) {
            return;
        }

        if (!criteriaChangedOnceRef.current[id]) {
            criteriaChangedOnceRef.current[id] = true;
            onCriteriaSettingsOpen(id);
        }
    };

    const handleClose = () => {
        setOpen(false);
        setModalContent(<></>);
    };

    const onCriteriaSettingsOpen = (id: string) => {
        const field = criteriaData.fields.find(f => f.id === id);
        if (!field || field.hide_field) {
            return;
        }
        setOpen(true);
        setModalContent(
            <CriteriaSetting
                field={field}
                onSliderChange={onSliderChange}
                onTradeoffValueChange={onTradeoffValueChange}
                fieldInitialValues={fieldInitialValues}
                handleModalClose={handleClose}
                areCTAsDisplayed
            />,
        );
    };

    return (
        <div className="application skin7">
            {criteriaLoading ? (
                <Loading/>
            ) : (
                <>
                    <AppInfo
                        icon={criteriaData.icon}
                        name={criteriaData.name}
                        description={criteriaData.description}
                    />
                    <div className="application-details">
                        <div className="app-criteria" id="app-criteria">
                            {criteriaLoading ? (
                                <Loading/>
                            ) : (
                                <div className="app-criteria-bubble-chart" ref={bubbleChartRef}>
                                    <BubbleChart
                                        initialCriteriaFields={getCriteriaFieldsToDisplayInCriteria(criteriaData.fields)}
                                        onCriteriaApply={() => {
                                            onCriteriaApply(dataset);
                                            resultsDivRef.current?.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                        onTradeoffValueChange={onTradeoffValueChange}
                                        onSliderChange={onSliderChange}
                                        onCriteriaSettingsOpen={onCriteriaSettingsOpen}
                                        onAutoPopupCheck={onAutoPopupCheck}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="app-results skin6-products" ref={resultsDivRef}>
                            {resultLoading ? (
                                <Loading/>
                            ) : (
                                <AppResults
                                    results={results}
                                    pageInfo={pageInfo}
                                    onPageChange={onPageChange}
                                    onRerank={onRerank}
                                    onRerankMany={onRerankMany}
                                    shouldShowRankByManyCheckbox={shouldShowRankByManyCheckbox}
                                    selectedProductIds={selectedProductIds}
                                    setSelectedProductIds={setSelectedProductIds}
                                    allowedFieldsIds={displayFieldsIds}
                                    criteriaData={criteriaData}
                                    criteriaConstraints={criteriaConstraints}
                                />
                            )}
                        </div>
                    </div>
                </>
            )}
            <Modal isOpen={open} handleClose={handleClose}>{modalContent}</Modal>
        </div>
    );
};

export default AppSkin7;